import React, { useState } from 'react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedTextarea } from '../../../validation/ValidatedFields';

interface RejectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (comment: string) => Promise<void> | void;
  /** Header label — defaults to "Send Back for Rework". */
  title?: string;
  /** Confirm button label — defaults to "Send Back". */
  confirmLabel?: string;
  /** Minimum trimmed comment length required to enable Submit. */
  minLength?: number;
}

/**
 * Modal that captures a mandatory reason comment before approver rejects
 * (sends back) a procedure or program instance.
 *
 * Validation: trimmed comment must be >= minLength characters (default 15).
 * The Send Back button is disabled until that threshold is met. After
 * pressing it, onConfirm(comment) fires and the dialog awaits the result
 * (showing a "Submitting…" label) before closing on success.
 */
const RejectDialog: React.FC<RejectDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Send Back for Rework',
  confirmLabel = 'Send Back',
  minLength = 15,
}) => {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const vh = useFieldValidation();

  const trimmed = comment.trim();
  const canSubmit = trimmed.length >= minLength && !submitting;

  const handleClose = () => {
    if (submitting) return;
    setComment('');
    setError(null);
    vh.reset();
    onClose();
  };

  const handleSubmit = async () => {
    const engineOk = vh.validateAll([{ name: 'comment', code: 110, value: comment, required: true }]);
    if (!engineOk) return;
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(trimmed);
      setComment('');
      setSubmitting(false);
      onClose();
    } catch (e: any) {
      setSubmitting(false);
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to send back.');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" data-testid="MOD_reject_dialog">
      <Modal.Header onClose={handleClose}>{title}</Modal.Header>
      <Modal.Body>
        <ValidatedTextarea
          vh={vh}
          name="comment"
          code={110}
          required
          label="Reason for sending back"
          rows={5}
          data-testid="INP_reject_dialog_comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        {error && <div className="warning-text" role="alert" style={{ marginTop: 4 }}>{error}</div>}
        <div className={`char-counter${trimmed.length >= minLength ? ' char-counter--ok' : ''}`}>
          {trimmed.length}/{minLength} characters minimum
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" data-testid="BTN_reject_dialog_cancel" onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="delete-outline" data-testid="BTN_reject_dialog_submit" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Submitting...' : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RejectDialog;
